import React from "react";
import { Box, TableCell, Typography } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import WarningIcon from "@icons/myicons/warning-circle.svg?react";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { InvestmentInfo, RHFTextField, TableRow, Tooltip } from "@components";
import { ButtonWithActionMenu } from "@components/ButtonWithActionMenu";
import { ActionMenu } from "@features/csvDrop/ActionMenu";
import { parseOrUndefined, toCurrency } from "@helpers";
import { CsvCashTransactionRow } from "@bn/api/apiv3";
import { shouldIgnoreFieldError } from "@features/csvDrop/shouldIgnoreFieldError";

const getDate = (date: string | undefined) =>
  date && isValid(parseISO(date))
    ? format(new Date(date), "dd.MM.yyyy")
    : undefined;

type Props = {
  row: CsvCashTransactionRow;
  onEdit: (value: CsvCashTransactionRow) => void;
  onEditClick: (row: CsvCashTransactionRow) => void;
  onDeleteClick: (row: CsvCashTransactionRow) => void;
};

export const AccountsParsedRow = ({
  row,
  onEdit,
  onEditClick,
  onDeleteClick,
}: Props) => {
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    purpose: Yup.string().required(t("form.errors.required")),
  });

  const { control } = useForm<{
    purpose: string;
  }>({
    resolver: yupResolver(schema),
    defaultValues: {
      purpose: row.purpose?.value,
    },
  });

  const errors: string[] = Object.values(row).reduce((acc, field) => {
    if (field == null || typeof field !== "object") return acc;
    if (shouldIgnoreFieldError(field.name)) return acc;

    return field.error
      ? [
          ...acc,
          t("csvImport.cashParsingResults.error", {
            name: t(`csvImport.cashParsingResults.${field.name}`),
          }),
        ]
      : acc;
  }, []);

  const onPurposeChange = (value: string) =>
    onEdit({
      ...row,
      purpose: {
        ...row.purpose,
        value,
      },
    });

  return (
    <TableRow key={`${row.bank_booking_date}`}>
      <TableCell>
        <Box display="flex" alignItems="center" gap={2.5} mb={0.5}>
          {Boolean(errors.length) && (
            <Tooltip
              text={
                <Typography
                  variant="body3"
                  color="textSecondary"
                  fontWeight={400}
                >
                  {errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </Typography>
              }
            >
              <Box display="flex" alignItems="center">
                <WarningIcon />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Typography variant="body3" color="textSecondary" fontWeight={400}>
          {getDate(row.bank_booking_date?.value)}
        </Typography>
      </TableCell>
      <TableCell>
        <InvestmentInfo
          item={{
            name: row.counterpart_name?.value,
            isin: row.counterpart_iban?.value,
            hasValidIsin: true,
          }}
        />
      </TableCell>
      <TableCell align="right">
        <RHFTextField
          fullWidth
          control={control}
          name="purpose"
          value={row.purpose?.value}
          onChange={(e) => onPurposeChange(e.target.value)}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body3" align="right">
          {toCurrency(parseOrUndefined(row.amount?.value ?? "") ?? 0, "EUR")}
        </Typography>
      </TableCell>
      <TableCell>
        <ButtonWithActionMenu>
          <ActionMenu
            onEditClick={() => onEditClick(row)}
            onDeleteClick={() => onDeleteClick(row)}
          />
        </ButtonWithActionMenu>
      </TableCell>
    </TableRow>
  );
};
